import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Moon, Sun, Menu, X, ArrowRight, ShoppingBag } from 'lucide-react';
import { useTheme } from '@/hooks/useTheme';
import { useCart } from '@/context/CartContext';

const NAV_LINKS = [
  { label: 'Services', to: '/services' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'Shop', to: '/shop' },
  { label: 'Blog', to: '/blog' },
  { label: 'FAQ', to: '/faq' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [shopVisible, setShopVisible] = useState(true);
  const { dark, toggle } = useTheme();
  const location = useLocation();
  const { count, setDrawerOpen } = useCart();

  useEffect(() => {
    let lastY = window.scrollY;
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled(y > 20);
        const delta = y - lastY;
        if (delta > 5 && y > 80) setHidden(true);
        else if (delta < -3) setHidden(false);
        lastY = y;
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/shop/settings`)
      .then(r => r.json())
      .then(d => { if (d.shop_visible === false) setShopVisible(false); })
      .catch(() => {});
  }, []);

  const navLinks = shopVisible
    ? [{ label: 'Services', to: '/services' }, { label: 'Pricing', to: '/pricing' }, { label: 'Shop', to: '/shop' }, { label: 'Blog', to: '/blog' }, { label: 'FAQ', to: '/faq' }]
    : [{ label: 'Services', to: '/services' }, { label: 'Pricing', to: '/pricing' }, { label: 'Blog', to: '/blog' }, { label: 'FAQ', to: '/faq' }];

  const isActive = (to) => location.pathname === to;

  // Pages with dark hero headers need light nav text when not scrolled
  const darkHeaderPage = ['/blog', '/faq', '/privacy-policy', '/terms-of-service'].includes(location.pathname) || location.pathname.startsWith('/blog/');
  const lightNav = darkHeaderPage && !scrolled;

  return (
    <nav
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        hidden && !mobileOpen ? '-translate-y-full' : 'translate-y-0'
      } ${
        scrolled
          ? 'glass-nav border-b border-slate-200/50 dark:border-white/5 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo — always goes home */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[#00E5FF] group-hover:scale-110 transition-transform">
              <span className="font-['Outfit'] font-black text-[#0A0A0A] text-sm leading-none">Ui</span>
            </div>
            <span className={`font-['Outfit'] font-bold text-lg tracking-tight ${lightNav ? 'text-white' : 'text-[#0A0A0A] dark:text-white'}`}>
              UI DESIGNS
            </span>
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors link-underline ${
                  isActive(link.to)
                    ? (lightNav ? 'text-[#00E5FF]' : 'text-[#0A0A0A] dark:text-white')
                    : (lightNav ? 'text-slate-300 hover:text-white' : 'text-slate-600 dark:text-slate-400 hover:text-[#0A0A0A] dark:hover:text-white')
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right side: theme toggle + cart + CTA */}
          <div className="hidden md:flex items-center gap-4">
            <button
              data-testid="theme-toggle"
              onClick={toggle}
              className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${lightNav ? 'border-white/20 bg-white/10 hover:bg-white/20' : 'border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-violet-400/10'}`}
            >
              {dark ? (
                <Sun className="w-4 h-4 text-[#00E5FF]" />
              ) : (
                <Moon className={`w-4 h-4 ${lightNav ? 'text-slate-300' : 'text-slate-600'}`} />
              )}
            </button>
            {shopVisible && (
              <button
                data-testid="cart-toggle"
                onClick={() => setDrawerOpen(true)}
                className={`w-9 h-9 rounded-full flex items-center justify-center border transition-colors relative ${lightNav ? 'border-white/20 bg-white/10 hover:bg-white/20' : 'border-slate-200 dark:border-white/10 bg-white dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-violet-400/10'}`}
              >
                <ShoppingBag className={`w-4 h-4 ${lightNav ? 'text-slate-300' : 'text-slate-600 dark:text-slate-400'}`} />
                {count > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#00E5FF] text-[#0A0A0A] text-[9px] font-bold rounded-full flex items-center justify-center" data-testid="cart-count">{count}</span>
                )}
              </button>
            )}
            <Link
              to="/contact"
              data-testid="nav-cta"
              className="inline-flex items-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#00E5FF] hover:text-[#0A0A0A] dark:hover:bg-violet-400 transition-all"
            >
              Get in Touch
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Mobile: theme + cart + hamburger */}
          <div className="flex md:hidden items-center gap-3">
            <button
              data-testid="theme-toggle-mobile"
              onClick={toggle}
              className={`w-9 h-9 rounded-full flex items-center justify-center border ${lightNav ? 'border-white/20' : 'border-slate-200 dark:border-white/10'}`}
            >
              {dark ? <Sun className="w-4 h-4 text-[#00E5FF]" /> : <Moon className={`w-4 h-4 ${lightNav ? 'text-slate-300' : 'text-slate-600'}`} />}
            </button>
            {shopVisible && (
              <button
                data-testid="cart-toggle-mobile"
                onClick={() => setDrawerOpen(true)}
                className={`w-9 h-9 rounded-full flex items-center justify-center border relative ${lightNav ? 'border-white/20' : 'border-slate-200 dark:border-white/10'}`}
              >
                <ShoppingBag className={`w-4 h-4 ${lightNav ? 'text-slate-300' : 'text-slate-600 dark:text-slate-400'}`} />
                {count > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#00E5FF] text-[#0A0A0A] text-[9px] font-bold rounded-full flex items-center justify-center">{count}</span>}
              </button>
            )}
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(!mobileOpen)}
              className={`w-9 h-9 flex items-center justify-center ${lightNav ? 'text-white' : 'text-[#0A0A0A] dark:text-white'}`}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden border-t border-slate-100 dark:border-white/10 py-4 pb-6 bg-white/95 dark:bg-[#0A0A0A]/95 backdrop-blur-xl"
          >
            <div className="flex flex-col gap-1">
              <Link
                to="/"
                className={`text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  isActive('/') ? 'text-[#0A0A0A] dark:text-white bg-slate-50 dark:bg-white/5' : 'text-slate-600 dark:text-slate-400 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-slate-50 dark:hover:bg-violet-400/5'
                }`}
              >
                Home
              </Link>
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`text-left px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    isActive(link.to) ? 'text-[#0A0A0A] dark:text-white bg-slate-50 dark:bg-white/5' : 'text-slate-600 dark:text-slate-400 hover:text-[#0A0A0A] dark:hover:text-white hover:bg-slate-50 dark:hover:bg-violet-400/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
            <div className="px-4 pt-5 mt-4 border-t border-slate-100 dark:border-white/10 pb-2">
              <Link
                to="/contact"
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] dark:bg-[#8B5CF6] text-white px-5 py-3 rounded-full text-sm font-medium"
              >
                Get in Touch
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
